import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ACTIVITY_STATUS_LABEL, UNIT_RAT_STATUS_LABEL, type ActivityStatus, type UnitRatStatus } from '@rat/shared';
import { fetchActivities, fetchUnitDashboard, transitionUnit } from '../lib/queries';
import { useAuth } from '../auth/AuthProvider';
import { canIn } from '../auth/access';
import { allowedUnitTransitions } from '../lib/workflowPerms';
import { can } from '@rat/shared';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ActivityStatusPill, PageHeader, ProgressBar, QueryState, UnitStatusPill } from '../components/ui';
import { AttentionTable, RiskKpis } from '../components/DashboardWidgets';

export function UnitDashboard() {
  const { unitId = '' } = useParams();
  const { access, authz } = useAuth();
  const qc = useQueryClient();

  const dash = useQuery({ queryKey: ['dash', 'unit', unitId], queryFn: () => fetchUnitDashboard(unitId) });
  const acts = useQuery({
    queryKey: ['activities', 'unit', unitId],
    queryFn: () => fetchActivities({ unitId, limit: 100 }),
  });

  // Solo las transiciones que el rol puede ejecutar EN esta unidad (la unidad ENVIA, el revisor DECIDE, solo el
  // administrador reabre). Antes se ofrecian todas y el backend rechazaba con 403 las que el jefe no puede hacer.
  const nextStates: UnitRatStatus[] = useMemo(
    () => (dash.data ? allowedUnitTransitions(access, dash.data.rat_status as UnitRatStatus, unitId) : []),
    [dash.data, access, unitId],
  );
  const isChief = canIn(access, 'unit.status.submit', unitId);

  return (
    <>
      {/* El tablero institucional solo se ofrece como miga a quien puede verlo: al resto no le llevaria a ninguna parte. */}
      <Breadcrumbs
        items={[
          ...(can(authz, 'institutional.view') || can(authz, 'unit.read.all') ? [{ label: 'Tablero institucional', to: '/institucional' }] : []),
          { label: dash.data?.unit_name ?? 'Unidad' },
        ]}
      />
      <PageHeader
        eyebrow={dash.data?.unit_name}
        title="Tablero de la unidad"
        sub={dash.data ? `${dash.data.activities_total} actividades` : undefined}
        actions={
          <Link className="btn" to={`/reportes/unidad/${unitId}`}>
            Exportar reporte
          </Link>
        }
      />

      <QueryState isLoading={dash.isLoading} error={dash.error}>
        {dash.data && (
          <>
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card__b" style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <div className="eyebrow">Estado de la unidad</div>
                  <div style={{ marginTop: 4 }}>
                    <UnitStatusPill status={dash.data.rat_status as UnitRatStatus} />
                  </div>
                </div>
                <div>
                  <div className="eyebrow">Avance</div>
                  <div style={{ fontFamily: 'var(--f-display)', fontSize: 24, fontWeight: 600 }}>
                    {dash.data.completeness_avg}%
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <ProgressBar pct={dash.data.completeness_avg} />
                </div>
              </div>

              {nextStates.length === 0 && isChief && (
                <div className="card__b" style={{ borderTop: '1px solid var(--border)' }}>
                  <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                    En el estado «{UNIT_RAT_STATUS_LABEL[dash.data.rat_status as UnitRatStatus]}» el siguiente cambio de la
                    unidad lo decide el equipo revisor.
                  </p>
                </div>
              )}
              {nextStates.length > 0 && (
                <div className="card__b" style={{ borderTop: '1px solid var(--border)' }}>
                  <ChangeUnitState
                    key={dash.data.rat_status /* al cambiar el estado, el formulario se reinicia: sus opciones son otras */}
                    unitId={unitId}
                    from={dash.data.rat_status as UnitRatStatus}
                    options={nextStates}
                    onDone={() => {
                      void qc.invalidateQueries({ queryKey: ['dash', 'unit', unitId] });
                      void qc.invalidateQueries({ queryKey: ['dash', 'inst'] });
                    }}
                  />
                </div>
              )}
            </div>

            <div className="kpis" style={{ marginBottom: 16 }}>
              <div
                className="kpi"
                style={dash.data.observaciones_abiertas > 0 ? { borderColor: 'var(--warning)', background: 'var(--warning-weak)' } : undefined}
              >
                <div className="n" style={dash.data.observaciones_abiertas > 0 ? { color: 'var(--warning)' } : undefined}>
                  {dash.data.observaciones_abiertas}
                </div>
                <div className="l">Observaciones abiertas</div>
              </div>
              {(Object.keys(dash.data.by_status) as ActivityStatus[])
                .filter((s) => dash.data!.by_status[s] > 0)
                .map((s) => (
                  <div className="kpi" key={s}>
                    <div className="n">{dash.data!.by_status[s]}</div>
                    <div className="l">{ACTIVITY_STATUS_LABEL[s]}</div>
                  </div>
                ))}
            </div>

            <div className="eyebrow" style={{ marginBottom: 8 }}>
              Riesgo y cumplimiento
            </div>
            <RiskKpis riesgo={dash.data.riesgo} />

            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card__h">
                <h3 style={{ fontSize: 15 }}>Necesitan atención</h3>
              </div>
              <div className="card__b">
                <AttentionTable rows={dash.data.atencion} />
              </div>
            </div>
          </>
        )}
      </QueryState>

      <QueryState isLoading={acts.isLoading} error={acts.error}>
        <div className="tablewrap">
          <table className="data">
            <thead>
              <tr>
                <th>Código</th>
                <th>Actividad</th>
                <th>Estado</th>
                <th>Completitud</th>
              </tr>
            </thead>
            <tbody>
              {(acts.data ?? []).map((a) => (
                <tr key={a.id}>
                  <td className="mono">{a.ref_code}</td>
                  <td>
                    <a href={`/actividades/${a.id}`}>{a.title || '(sin nombre)'}</a>
                  </td>
                  <td>
                    <ActivityStatusPill status={a.status} />
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ProgressBar pct={a.completeness_pct} />
                      <span className="mono">{a.completeness_pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
              {acts.data?.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted" style={{ padding: 24 }}>
                    Esta unidad aún no registra actividades de tratamiento.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </QueryState>
    </>
  );
}

function ChangeUnitState({
  unitId,
  from,
  options,
  onDone,
}: {
  unitId: string;
  from: UnitRatStatus;
  options: UnitRatStatus[];
  onDone: () => void;
}) {
  const [to, setTo] = useState<UnitRatStatus>(options[0]!);
  const m = useMutation({
    mutationFn: () => transitionUnit(unitId, to),
    onSuccess: onDone,
  });

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div className="field" style={{ marginBottom: 0 }}>
        <label>Cambiar estado de la unidad (desde {UNIT_RAT_STATUS_LABEL[from]})</label>
        <select value={to} onChange={(e) => setTo(e.target.value as UnitRatStatus)}>
          {options.map((o) => (
            <option key={o} value={o}>
              {UNIT_RAT_STATUS_LABEL[o]}
            </option>
          ))}
        </select>
      </div>
      <button className="btn btn--primary" disabled={m.isPending} onClick={() => m.mutate()}>
        {m.isPending ? 'Aplicando…' : 'Aplicar'}
      </button>
      {m.error && (
        <div className="callout callout--crit" style={{ flexBasis: '100%' }}>
          {(m.error as Error).message}
        </div>
      )}
    </div>
  );
}
